import React, { useEffect, useMemo, useState } from 'react';
import { useCommandsStore } from '../../store/commandsStore';
import { Button } from '../ui/button';
import { Tabs, TabsList, TabsTrigger } from '../ui/tabs';
import { X, Sparkles, GripVertical, FileText, Image as ImageIcon, FileSearch } from 'lucide-react';
import ContentPreview from './ContentPreview';
import HistoryPanel from './HistoryPanel';
import TextoTab from './tabs/TextoTab';
import ImagenTab from './tabs/ImagenTab';
import DescripcionTab from './tabs/DescripcionTab';
import useDraggableModal from './hooks/useDraggableModal';

const QuickCommandModal = ({ selectedContent, position, onClose, onAction }) => {
  const { commands } = useCommandsStore();

  const content = typeof selectedContent === 'string'
    ? { text: selectedContent, images: [] }
    : selectedContent || { text: '', images: [] };

  const hasText = content.text && content.text.trim().length > 0;
  const hasImages = content.images && content.images.length > 0;

  const getDefaultTab = () => {
    if (hasImages && !hasText) return 'descripcion';
    return 'texto';
  };

  const [activeTab, setActiveTab] = useState(getDefaultTab());
  const [message, setMessage] = useState('');
  const [selectedCommand, setSelectedCommand] = useState('');
  const [loading, setLoading] = useState(false);
  const [aiResponse, setAiResponse] = useState('');
  const [showCustomMessage, setShowCustomMessage] = useState(false);
  const [imageDescription, setImageDescription] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [describeAllImages, setDescribeAllImages] = useState(false);
  const [externalImages, setExternalImages] = useState([]);
  const [generationHistory, setGenerationHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [isDraggingImage, setIsDraggingImage] = useState(false);

  const { modalRef, modalPosition, isDragging, handleMouseDown } = useDraggableModal(position);

  const textoCommands = useMemo(
    () => commands.filter((cmd) => cmd.category === 'Texto' || !cmd.category),
    [commands]
  );
  const imagenCommands = useMemo(
    () => commands.filter((cmd) => cmd.category === 'Imagen'),
    [commands]
  );
  const descripcionCommands = useMemo(
    () => commands.filter((cmd) => cmd.category === 'Descripción'),
    [commands]
  );

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') onClose({ reason: 'escape' });
    };

    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  const toggleCustomMessage = () => setShowCustomMessage((prev) => !prev);
  const resetAiResponse = () => setAiResponse('');

  const allImages = [...(content.images || []), ...externalImages];
  const hasAnyImages = allImages.length > 0;

  const handleSend = async () => {
    if (activeTab === 'texto') {
      if (!message && !selectedCommand) return;

      setLoading(true);

      const command = commands.find((c) => c.id === selectedCommand);

      // T-034: a command with 2+ steps is a macro — hand the whole command to the
      // sequential orchestrator (single editor lock, step chaining). One-step
      // commands (and free-text messages) keep the classic one-shot path.
      // T-231: a 1-step command that declares a per-step model or advisors is
      // ALSO routed through the macro orchestrator (the only path that honors them).
      const hasStepConfig = command && Array.isArray(command.steps) &&
        command.steps.some((s) => ((s.model || '').trim() || (Array.isArray(s.advisors) && s.advisors.length > 0)));
      const isMacro = command && Array.isArray(command.steps) && (command.steps.length >= 2 || hasStepConfig);

      try {
        if (isMacro) {
          await onAction('macro', command, content.text, (response) => {
            setAiResponse(response);
            addToHistory('texto', `Macro: ${command.title}`, response);
          });
        } else {
          const instruction = command
            ? `${command.instruction}\n\nTexto:\n${content.text}`
            : message;
          await onAction('process', instruction, content.text, (response) => {
            setAiResponse(response);
            addToHistory('texto', instruction, response);
          });
        }
      } catch (error) {
        console.error('Error processing command:', error);
      } finally {
        setLoading(false);
      }
    } else if (activeTab === 'imagen') {
      if (!imageDescription && !selectedCommand && !content.text) return;

      setLoading(true);

      try {
        const command = commands.find((c) => c.id === selectedCommand);
        const baseInstruction = command ? command.instruction : 'Genera una imagen basada en la siguiente descripción:';
        const textToUse = imageDescription || content.text || '';
        const customMsg = message ? `\n\nInstrucciones adicionales: ${message}` : '';
        const fullInstruction = `${baseInstruction}\n\n${textToUse}${customMsg}`;

        await onAction('generateImage', fullInstruction, textToUse, (response) => {
          setAiResponse(response);
          addToHistory('imagen', fullInstruction, response);
        });
      } catch (error) {
        console.error('Error generating image:', error);
      } finally {
        setLoading(false);
      }
    } else if (activeTab === 'descripcion') {
      const targetImageUrl = hasAnyImages ? allImages[selectedImageIndex]?.src : null;

      if (!targetImageUrl && (!message && !selectedCommand)) return;

      setLoading(true);

      try {
        if (describeAllImages && hasAnyImages) {
          const descriptions = [];
          for (let i = 0; i < allImages.length; i += 1) {
            const command = commands.find((c) => c.id === selectedCommand);
            const instruction = command ? command.instruction : (message || 'Describe la imagen en detalle.');

            await onAction('describeImage', instruction, allImages[i].src, (response) => {
              descriptions.push(`**Imagen ${i + 1}:**\n${response}`);
            });
          }
          const finalResult = descriptions.join('\n\n');
          setAiResponse(finalResult);
          addToHistory('descripcion', `Descripción de ${allImages.length} imágenes`, finalResult);
        } else {
          const command = commands.find((c) => c.id === selectedCommand);
          const instruction = command ? command.instruction : (message || 'Describe la imagen en detalle.');

          await onAction('describeImage', instruction, targetImageUrl, (response) => {
            setAiResponse(response);
            addToHistory('descripcion', instruction, response);
          });
        }
      } catch (error) {
        console.error('Error describing image:', error);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleInsert = () => {
    if (aiResponse) {
      onAction('insert', aiResponse, '');
      onClose();
    }
  };

  const handleReplace = () => {
    if (aiResponse) {
      onAction('replace', aiResponse, content.text);
      onClose();
    }
  };

  const addToHistory = (type, prompt, response) => {
    const historyItem = {
      id: Date.now(),
      type,
      prompt,
      response,
      timestamp: new Date().toISOString()
    };
    setGenerationHistory((prev) => [historyItem, ...prev].slice(0, 10));
  };

  const loadFromHistory = (item) => {
    setAiResponse(item.response);
    setActiveTab(item.type);
  };

  const addExternalImage = () => {
    if (imageUrl && imageUrl.trim()) {
      setExternalImages((prev) => [
        ...prev,
        {
          src: imageUrl.trim(),
          alt: `Imagen externa ${prev.length + 1}`,
          isExternal: true
        }
      ]);
      setImageUrl('');
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingImage(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingImage(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingImage(false);

    const url = e.dataTransfer.getData('text/plain');
    if (url && (url.startsWith('http://') || url.startsWith('https://'))) {
      setExternalImages((prev) => [
        ...prev,
        {
          src: url,
          alt: `Imagen externa ${prev.length + 1}`,
          isExternal: true
        }
      ]);
    }
  };

  return (
    <>
      <div
        className="fixed inset-0 bg-black/20 z-40 animate-in fade-in duration-200"
        onClick={() => onClose({ reason: 'backdrop' })}
        onMouseDown={(e) => {
          e.stopPropagation();
        }}
      />

      <div
        ref={modalRef}
        className="fixed z-50 w-[90vw] sm:w-[600px] max-w-[600px] glass rounded-xl shadow-2xl flex flex-col overflow-hidden user-select-none"
        style={{
          top: `${modalPosition.top}px`,
          left: `${modalPosition.left}px`,
          maxHeight: 'calc(100vh - 40px)',
          cursor: isDragging ? 'grabbing' : 'default'
        }}
        data-testid="quick-command-modal"
        onMouseDown={handleMouseDown}
      >
        <div className="drag-handle cursor-grab active:cursor-grabbing bg-slate-800 text-white p-3 sm:p-4 rounded-t-xl border-b border-slate-700 flex items-center justify-between select-none flex-shrink-0">
          <div className="flex items-center gap-2">
            <GripVertical className="w-4 h-4 sm:w-5 sm:h-5 text-white flex-shrink-0" />
            <Sparkles className="w-4 h-4 sm:w-5 sm:h-5 text-white flex-shrink-0" />
            <h3 className="font-semibold text-base sm:text-lg truncate">Acción Rápida IA</h3>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onClose({ reason: 'button' })}
            className="h-8 w-8 p-0 hover:bg-slate-700 flex-shrink-0"
          >
            <X className="w-4 h-4" />
          </Button>
        </div>

        <div className="p-3 sm:p-4 overflow-y-auto flex-1 min-h-0">
          <ContentPreview content={content} hasText={hasText} hasImages={hasImages} />

          <HistoryPanel
            generationHistory={generationHistory}
            showHistory={showHistory}
            onToggleHistory={() => setShowHistory((prev) => !prev)}
            onLoadFromHistory={loadFromHistory}
          />

          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid w-full grid-cols-3 mb-4">
              <TabsTrigger
                value="texto"
                disabled={!hasText}
                className="flex items-center gap-1 sm:gap-2 disabled:opacity-50 disabled:cursor-not-allowed text-xs sm:text-sm"
              >
                <FileText className="w-3 h-3 sm:w-4 sm:h-4" />
                <span className="hidden sm:inline">Texto</span>
                <span className="sm:hidden">Txt</span>
              </TabsTrigger>
              <TabsTrigger value="imagen" className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm">
                <ImageIcon className="w-3 h-3 sm:w-4 sm:h-4" />
                <span className="hidden sm:inline">Imagen</span>
                <span className="sm:hidden">Img</span>
              </TabsTrigger>
              <TabsTrigger value="descripcion" className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm">
                <FileSearch className="w-3 h-3 sm:w-4 sm:h-4" />
                <span className="hidden sm:inline">Describir</span>
                <span className="sm:hidden">Desc</span>
                {allImages.length > 0 && (
                  <span className="ml-1 px-1.5 py-0.5 text-[10px] sm:text-xs bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-300 rounded-full">
                    {allImages.length}
                  </span>
                )}
              </TabsTrigger>
            </TabsList>

            <TextoTab
              aiResponse={aiResponse}
              loading={loading}
              hasText={hasText}
              selectedCommand={selectedCommand}
              onCommandChange={setSelectedCommand}
              textoCommands={textoCommands}
              showCustomMessage={showCustomMessage}
              onToggleCustomMessage={toggleCustomMessage}
              message={message}
              onMessageChange={setMessage}
              onSend={handleSend}
              onInsert={handleInsert}
              onReplace={handleReplace}
              onResponseChange={setAiResponse}
              onResetResponse={resetAiResponse}
            />

            <ImagenTab
              aiResponse={aiResponse}
              loading={loading}
              content={content}
              hasText={hasText}
              selectedCommand={selectedCommand}
              onCommandChange={setSelectedCommand}
              imagenCommands={imagenCommands}
              imageDescription={imageDescription}
              onImageDescriptionChange={setImageDescription}
              showCustomMessage={showCustomMessage}
              onToggleCustomMessage={toggleCustomMessage}
              message={message}
              onMessageChange={setMessage}
              onSend={handleSend}
              onInsert={handleInsert}
              onResponseChange={setAiResponse}
              onResetResponse={resetAiResponse}
            />

            <DescripcionTab
              aiResponse={aiResponse}
              loading={loading}
              selectedCommand={selectedCommand}
              onCommandChange={setSelectedCommand}
              descripcionCommands={descripcionCommands}
              showCustomMessage={showCustomMessage}
              onToggleCustomMessage={toggleCustomMessage}
              message={message}
              onMessageChange={setMessage}
              hasAnyImages={hasAnyImages}
              allImages={allImages}
              selectedImageIndex={selectedImageIndex}
              onSelectImage={setSelectedImageIndex}
              describeAllImages={describeAllImages}
              onToggleDescribeAll={(value) => setDescribeAllImages(Boolean(value))}
              imageUrl={imageUrl}
              onImageUrlChange={setImageUrl}
              onAddExternalImage={addExternalImage}
              isDraggingImage={isDraggingImage}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onSend={handleSend}
              onInsert={handleInsert}
              onResponseChange={setAiResponse}
              onResetResponse={resetAiResponse}
            />
          </Tabs>
        </div>
      </div>
    </>
  );
};

export default QuickCommandModal;
